import { SerializedSocialChannel } from '../types';

/**
 * What the Channels card and the Performance totals show for the numbers of a channel
 * (design social-connectors, "State rules").
 *
 * - `reconnect`: the platform ended the sign-in. The last numbers stay, dimmed.
 * - `reconnectForNumbers`: the sign-in lacks a read scope. Posting still works.
 * - `reading`: the first daily read did not finish.
 * - `numbers`: the daily read runs. A failed read keeps the last numbers.
 * - `none`: a manual or offline channel. A dash, no line.
 */
export type ChannelNumbersState = 'reconnect' | 'reconnectForNumbers' | 'reading' | 'numbers' | 'none';

type ChannelReadFields = Pick<
  SerializedSocialChannel,
  'status' | 'syncStatus' | 'needsReconnect' | 'canReadNumbers' | 'lastSyncedAt'
>;

export function channelNumbersState(channel: ChannelReadFields): ChannelNumbersState {
  if (channel.syncStatus === 'reauthRequired') {
    return 'reconnect';
  }
  if (channel.status !== 'connected') {
    return 'none';
  }
  if (channel.needsReconnect) {
    return 'reconnectForNumbers';
  }
  if (!channel.canReadNumbers) {
    return 'none';
  }
  return channel.lastSyncedAt === null ? 'reading' : 'numbers';
}

/** A channel with a card in the Performance totals: connected, or waiting for a new sign-in. */
export function channelHasSocialNumbers(channel: ChannelReadFields): boolean {
  return channelNumbersState(channel) !== 'none';
}

export type FollowersPillKind = 'hidden' | 'firstWeek' | 'change';

/**
 * The change pill of the card head. It hides when the platform gives no follower count. It
 * reads "first week" when the server has no change to give: the channel has one count only
 * (design a2, state 2). A window with fewer daily rows than days does not hide a real change.
 */
export function followersPillKind(
  channel: Pick<SerializedSocialChannel, 'followers' | 'followersChange'>
): FollowersPillKind {
  if (channel.followers === null) {
    return 'hidden';
  }
  return channel.followersChange === null ? 'firstWeek' : 'change';
}
